import { memo, useState, useCallback, useRef } from 'react';
import { CaseReportStatementsList } from '@/components/casereport/CaseReportStatementsList';
import { CaseReportEvidenceList } from '@/components/casereport/CaseReportEvidenceList';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';
import { Codes } from '@/constants/codes';

import { uploadBlockBlob } from '@/services/azure-blob-service';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
import { load } from '@/services/loading';

import { api } from '@/services/api-class';

function emptyStatementForm() {
  return { tempid: null as any, personId: null as any, personName: '', roles: [] as any[], statement: '' };
}

/** rdigital_evidence.title is a 255-character column; a longer title failed the whole report save. */
const EVIDENCE_TITLE_MAX_LENGTH = 255;

function emptyEvidenceForm() {
  return { tempid: null as any, title: '', evidenceOf: null as any, evidenceOfName: '', involving: null as any, involvingName: '', attachmentList: [] as any[], attachmentInfoList: [] as any[] };
}

interface Props {
  statementList: any[];
  setStatementList: (s: any[]) => void;
  digitalEvidenceList: any[];
  setDigitalEvidenceList: (e: any[]) => void;
  involvedPersonOptions: { id: any; name: string }[];
  involvedVehicleOptions: { id: any; name: string }[];
  businesses: any[];
  clients: any[];
  evidenceOfOptions: any[];
  allInvolvedOptions: { id: string; name: string }[];
  isReadOnly: boolean;
}

export const CaseReportEvidenceSection = memo(function CaseReportEvidenceSection({
  statementList, setStatementList, digitalEvidenceList, setDigitalEvidenceList,
  involvedPersonOptions, involvedVehicleOptions, businesses, clients,
  evidenceOfOptions, allInvolvedOptions, isReadOnly,
}: Props) {
  // Statement modal
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementForm, setStatementForm] = useState(emptyStatementForm);

  // Evidence modal
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState(emptyEvidenceForm);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatRoles = useCallback((roles: any[]) => {
    if (!roles?.length) return '';
    const personCodes = Codes.getPersonCodes?.() || [];
    return roles.map((r: any) => {
      const code = personCodes.find((c: any) => c.code === r || c.code === Number(r));
      return code?.value || r;
    }).join(', ');
  }, []);

  // ── Statement handlers ────────────────────────────────────────────
  const openStatementModal = useCallback((stmt?: any) => {
    if (stmt) {
      let roles: any[] = [];
      const pid = stmt.npsdPerson;
      if (pid) {
        const p = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find(
          (pp: any) => pp.id === pid || pp.id === Number(pid));
        if (p?.roles) roles = p.roles;
      }
      setStatementForm({ tempid: stmt.tempid, personId: pid, personName: stmt.personName || '', roles, statement: stmt.statement || '' });
    } else {
      setStatementForm(emptyStatementForm());
    }
    setShowStatementModal(true);
  }, []);

  const saveStatement = useCallback(() => {
    const form = statementForm;
    if (!form.personId) { App.showPopupNotification('Person is required.', 'error'); return; }

    const dup = CaseRpt.currentMDTRpt.statementList.findIndex((n: any) =>
      n.npsdPerson?.toString() === form.personId.toString() && (!form.tempid || n.tempid !== form.tempid));
    if (dup > -1) { App.showPopupNotification('Person already has a statement.', 'warning'); return; }

    let roles = form.roles || [];
    const person = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find(
      (p: any) => p.id === form.personId || p.id === Number(form.personId));
    if (person?.roles) roles = person.roles;

    const entry = {
      tempid: form.tempid || getInsecureUUID(), npsdPerson: form.personId,
      personName: form.personName, personRoles: roles, statement: form.statement,
    };

    if (form.tempid) {
      const idx = CaseRpt.currentMDTRpt.statementList.findIndex((s: any) => s.tempid === form.tempid);
      if (idx > -1) CaseRpt.currentMDTRpt.statementList[idx] = entry;
    } else {
      CaseRpt.currentMDTRpt.statementList.push(entry);
    }
    setStatementList(CaseRpt.currentMDTRpt.statementList.slice());
    setShowStatementModal(false);
    setStatementForm(emptyStatementForm());
  }, [statementForm, setStatementList]);

  // ── Evidence handlers ─────────────────────────────────────────────
  const openEvidenceModal = useCallback((evidence?: any) => {
    setPendingFiles([]);
    if (evidence) {
      setEvidenceForm({
        tempid: evidence.tempid, title: evidence.title || '', evidenceOf: evidence.evidenceOf,
        evidenceOfName: evidence.evidenceOfName || '', involving: evidence.involving,
        involvingName: evidence.involvingName || '',
        attachmentList: evidence.attachmentList ? [...evidence.attachmentList] : [],
        attachmentInfoList: evidence.attachmentInfoList ? [...evidence.attachmentInfoList] : [],
      });
    } else {
      setEvidenceForm(emptyEvidenceForm());
    }
    setShowEvidenceModal(true);
  }, []);

  const saveEvidence = useCallback(() => {
    const form = evidenceForm;
    if (!form.title) { App.showPopupNotification('Title is required.', 'error'); return; }
    if (form.title.length > EVIDENCE_TITLE_MAX_LENGTH) {
      App.showPopupNotification(`Title must be ${EVIDENCE_TITLE_MAX_LENGTH} characters or fewer.`, 'error');
      return;
    }
    if (!form.evidenceOf) { App.showPopupNotification('Evidence Of is required.', 'error'); return; }
    if (!form.involving) { App.showPopupNotification('Involving is required.', 'error'); return; }

    let evidenceOfName = '';
    const eo = evidenceOfOptions.find((o: any) => o.code === form.evidenceOf);
    if (eo) evidenceOfName = eo.value || '';

    let involvingName = '';
    const io = allInvolvedOptions.find((o: any) => o.id === form.involving);
    if (io) involvingName = io.name || '';

    if (form.tempid) {
      const existing = CaseRpt.currentMDTRpt.digitalEvidenceList.find((e: any) => e.tempid === form.tempid);
      if (existing) {
        existing.title = form.title; existing.evidenceOf = form.evidenceOf;
        existing.evidenceOfName = evidenceOfName; existing.involving = form.involving;
        existing.involvingName = involvingName;
      }
      setDigitalEvidenceList(CaseRpt.currentMDTRpt.digitalEvidenceList.slice());
      setShowEvidenceModal(false);
      setEvidenceForm(emptyEvidenceForm());
    } else {
      if (pendingFiles.length === 0) {
        App.showPopupNotification('Please select at least one file to upload.', 'error'); return;
      }

      const files = pendingFiles;
      const attachmentList: any[] = files.map(f => {
        const a = new (window as any).Attachment();
        const dot = f.name.lastIndexOf('.');
        a.title = dot > 0 ? f.name.substring(0, dot) : f.name;
        a.filename = f.name;
        return a;
      });

      setShowEvidenceModal(false);
      setEvidenceForm(emptyEvidenceForm());
      setPendingFiles([]);
      App.uploadStarted(); App.uploadProgress(1); App.initBlobUploadProgressControl();

      api.saveAttachment(attachmentList, (result: any) => {
        App.uploadProgress(1);
        const total = result.azureStorageBlobInfoList.length;
        let completed = 0, success = 0;
        load.showLoadingScreen();

        for (let i = 0; i < total; i++) {
          App.blobUploadStarted();
          const file = files[i];
          const blob = result.azureStorageBlobInfoList[i];
          const signedURL = `${blob.host}/${blob.containerName}/${blob.blobName}?${blob.sasReadToken}`;

          const blockSize = file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;

          uploadBlockBlob(blob.host, blob.sasWriteToken, blob.containerName, blob.blobName, file, { blockSize }).then(() => {
            completed++; App.blobUploadCompleted();
            success++;
            CaseRpt.currentMDTRpt.digitalEvidenceList.push({
              tempid: getInsecureUUID(), title: form.title,
              evidenceOf: form.evidenceOf, evidenceOfName,
              involving: form.involving, involvingName,
              attachmentList: [{ id: blob.attachmentId, title: attachmentList[i].title, filename: file.name, signedURL }],
              attachmentInfoList: [{ id: blob.attachmentId, activationKey: blob.activationKey, fileName: file.name, signedURL }],
            });
            setDigitalEvidenceList(CaseRpt.currentMDTRpt.digitalEvidenceList.slice());
            if (completed >= total) {
              load.hideLoadingScreen();
              if (success > 0) App.showPopupNotification(success + ' evidence item(s) added.', 'success');
            }
          }).catch(() => {
            completed++; App.blobUploadCompleted();
            App.showPopupNotification('Error uploading: ' + file.name, 'error');
            if (completed >= total) {
              load.hideLoadingScreen();
              if (success > 0) App.showPopupNotification(success + ' evidence item(s) added.', 'success');
            }
          });
        }
      });
    }
  }, [evidenceForm, pendingFiles, evidenceOfOptions, allInvolvedOptions, setDigitalEvidenceList]);

  const handleDownloadEvidence = useCallback((attachment: any) => {
    if (attachment?.signedURL) window.open(attachment.signedURL, '_blank');
    else App.showPopupNotification('Unable to download. Please refresh.', 'warning');
  }, []);

  return (
    <>
      <div className="tab-panel">
        <CaseReportStatementsList
          value={statementList}
          isReadOnly={isReadOnly}
          involvedPersons={involvedPersonOptions}
          onChange={(s: any[]) => { CaseRpt.currentMDTRpt.statementList = s; setStatementList(s); }}
          onAdd={() => openStatementModal()}
          onEdit={(item: any) => openStatementModal(item)}
        />
        <CaseReportEvidenceList
          evidence={digitalEvidenceList}
          isReadOnly={isReadOnly}
          involvedPersons={involvedPersonOptions}
          involvedVehicles={involvedVehicleOptions}
          involvedBusinesses={businesses.map((b: any) => ({ id: b.id, name: b.businessName || b.name || 'Unknown' }))}
          involvedClients={clients.map((c: any) => ({ id: c.id, name: c.name || 'Unknown' }))}
          evidenceOfOptions={evidenceOfOptions}
          onChange={(e: any[]) => { CaseRpt.currentMDTRpt.digitalEvidenceList = e; setDigitalEvidenceList(e); }}
          onAdd={() => openEvidenceModal()}
          onEdit={(item: any) => openEvidenceModal(item)}
          onDownload={handleDownloadEvidence}
        />
      </div>

      {/* Statement Modal */}
      <ConfirmModal
        open={showStatementModal}
        title={statementForm.tempid ? 'Edit Statement' : 'Add Statement'}
        confirmText={statementForm.tempid ? 'Update Statement' : 'Add Statement'}
        confirmClass="primary"
        dialogClass="kstc-dialog--form"
        onConfirm={saveStatement}
        onCancel={() => { setShowStatementModal(false); setStatementForm(emptyStatementForm()); }}
      >
        <div className="form-group">
          <label className="mandatory"><b>Name</b></label>
          <Dropdownlist value={statementForm.personId} items={involvedPersonOptions}
            labelKey="name" valueKey="id" placeholder="Select person..."
            onChange={(val) => {
              const p = involvedPersonOptions.find((o: any) => o.id === val);
              let roles: any[] = [];
              if (val) {
                const conn = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find(
                  (pp: any) => pp.id === val || pp.id === Number(val));
                if (conn?.roles) roles = conn.roles;
              }
              setStatementForm(f => ({ ...f, personId: val, personName: p?.name || '', roles }));
            }} />
        </div>
        {statementForm.roles && statementForm.roles.length > 0 && (
          <div className="form-group">
            <label><b>Roles</b> (Modify on Involved Tab)</label>
            <div className="roles-display">{formatRoles(statementForm.roles)}</div>
          </div>
        )}
        <div className="form-group">
          <label><b>Statement</b></label>
          <textarea className="form-control" value={statementForm.statement}
            onChange={e => setStatementForm(f => ({ ...f, statement: e.target.value }))} rows={10} />
        </div>
      </ConfirmModal>

      {/* Evidence Modal */}
      <ConfirmModal
        open={showEvidenceModal}
        title={evidenceForm.tempid ? 'Edit Digital Evidence' : 'Add Digital Evidence'}
        confirmText={evidenceForm.tempid ? 'Update Digital Evidence' : 'Add Digital Evidence'}
        confirmClass="primary"
        dialogClass="kstc-dialog--form"
        onConfirm={saveEvidence}
        onCancel={() => { setShowEvidenceModal(false); setEvidenceForm(emptyEvidenceForm()); setPendingFiles([]); }}
      >
        <div className="form-group">
          <label className="mandatory"><b>Title</b></label>
          <input type="text" className="form-control" value={evidenceForm.title}
            maxLength={EVIDENCE_TITLE_MAX_LENGTH}
            onChange={e => setEvidenceForm(f => ({ ...f, title: e.target.value }))} />
          <span className="help-block">{evidenceForm.title.length} / {EVIDENCE_TITLE_MAX_LENGTH} characters</span>
        </div>
        <div className="form-group">
          <label className="mandatory"><b>Evidence Of</b></label>
          <Dropdownlist value={evidenceForm.evidenceOf} items={evidenceOfOptions}
            labelKey="value" valueKey="code" placeholder="Select type..."
            sortItems={false}
            onChange={(val) => setEvidenceForm(f => ({ ...f, evidenceOf: val }))} />
        </div>
        <div className="form-group">
          <label className="mandatory"><b>Involving</b></label>
          <Dropdownlist value={evidenceForm.involving} items={allInvolvedOptions}
            labelKey="name" valueKey="id" placeholder="Select..."
            onChange={(val) => setEvidenceForm(f => ({ ...f, involving: val }))} />
        </div>
        {!evidenceForm.tempid && (
          <div className="form-group">
            <label className="mandatory"><b>Upload</b></label>
            <p className="help-text" style={{ fontSize: '0.9rem', opacity: 0.75, marginBottom: '0.5rem' }}>
              Adding multiple uploads will create a separate digital evidence item for each upload.
            </p>
            <div className="file-upload-wrapper">
              <button type="button" className="btn-upload" onClick={() => fileInputRef.current?.click()}>
                <i className="fas fa-cloud-upload-alt"></i> Click Here to Upload
              </button>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    setPendingFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                    e.target.value = '';
                  }
                }} />
              {pendingFiles.length > 0 && (
                <div className="pending-files">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="pending-file">
                      <i className="fa fa-file"></i>
                      <span>{f.name}</span>
                      <button type="button" className="btn-remove-file"
                        onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {evidenceForm.tempid && evidenceForm.attachmentInfoList && evidenceForm.attachmentInfoList.length > 0 && (
          <div className="form-group">
            <label><b>Current Attachments</b></label>
            <div className="current-attachments">
              {evidenceForm.attachmentInfoList.map((att: any) => (
                <div key={att.id} className="attachment-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.5rem' }}>
                  <i className="fa fa-file"></i>
                  <span>{att.fileName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </ConfirmModal>
    </>
  );
});
